/**
 * /admin/profile — My Profile for the staff (ops) portal (telemetry-uat2).
 *
 * No profile page existed in portal-staff; this mirrors the subscriber My
 * Profile pattern against the staff record. Field support:
 *   - Name      — EDITABLE (staff display names live on the StaffTable DDB row,
 *                 not Cognito, so self-service edit needs no IAM); PUT /admin/me.
 *   - Email     — read-only (Cognito staff pool).
 *   - Role      — read-only (assigned by an admin; ADR-075).
 *   - Password  — functional, via Amplify updatePassword (client-side).
 *   - Timezone  — EDITABLE (NEW): tzPreference on the staff row; drives the
 *                 shared time formatter so every rendered instant uses the zone.
 *   - Preferred zone — EDITABLE (ADR-123 Decision 3): preferredZone on the staff
 *                 row; the zone every zone switcher and create target pre-selects.
 *                 Saved by the same PUT /admin/me. The ONLY place it changes — a
 *                 zone picked on any other page never writes it.
 *
 * Errors surface through the shared toast only — never raw, never inline.
 */
import { useEffect, useState } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import { useStaffMe } from '../../hooks/useStaffMe';
import { useToast, toastFromError } from '../../components/ToastProvider';
import { adminApi } from '../../lib/api-client';
import {
  COMMON_TIME_ZONES,
  useTzPreference,
  setTzPreference,
  deviceTimeZone,
} from '../../lib/time-format';
import {
  HOME_ZONE, ZONE_TABLE, consoleZoneLabel, setPreferredZone, usePreferredZone,
} from '../../lib/zones';
import './staff-profile.css';

const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin',
  nexa_admin: 'Nexa Admin',
  support: 'Support',
  read_only: 'Read Only',
  audit_only: 'Audit Only',
};

export function StaffProfilePage() {
  const meState = useStaffMe();
  const { push } = useToast();
  const storedPref = useTzPreference();
  const storedZone = usePreferredZone();

  const me = meState.status === 'loaded' ? meState.me : null;

  const [name, setName] = useState('');
  const [tz, setTz] = useState<string>(storedPref ?? '');
  const [zonePref, setZonePref] = useState<string>(storedZone ?? '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [showPw, setShowPw] = useState(false);

  // Seed the editable name once /admin/me resolves.
  useEffect(() => {
    if (me) setName(me.displayName ?? me.name ?? '');
  }, [me]);

  useEffect(() => {
    setTz(storedPref ?? '');
  }, [storedPref]);

  useEffect(() => {
    setZonePref(storedZone ?? '');
  }, [storedZone]);

  const originalName = me?.displayName ?? me?.name ?? '';
  const dirty = name.trim() !== originalName
    || (storedPref ?? '') !== tz
    || (storedZone ?? '') !== zonePref;

  async function saveProfile() {
    if (!dirty || savingProfile) return;
    setSavingProfile(true);
    try {
      await adminApi('/admin/me', {
        method: 'PUT',
        body: JSON.stringify({ displayName: name.trim(), tzPreference: tz, preferredZone: zonePref }),
      });
      setTzPreference(tz || null);
      setPreferredZone(zonePref || null);
      push({ variant: 'success', title: 'Profile saved', detail: 'Your profile has been updated.' });
    } catch (err) {
      push({ variant: 'error', ...toastFromError(err) });
    } finally {
      setSavingProfile(false);
    }
  }

  async function savePassword(e: React.FormEvent) {
    e.preventDefault();
    if (pwSaving) return;
    if (!curPw || !newPw) {
      push({ variant: 'error', title: 'Missing details', detail: 'Enter your current and new password.' });
      return;
    }
    if (newPw !== confirmPw) {
      push({ variant: 'error', title: 'Passwords do not match', detail: 'The new password and confirmation must match.' });
      return;
    }
    setPwSaving(true);
    try {
      await updatePassword({ oldPassword: curPw, newPassword: newPw });
      setCurPw(''); setNewPw(''); setConfirmPw('');
      push({ variant: 'success', title: 'Password changed', detail: 'Your password has been updated.' });
    } catch (err) {
      push({ variant: 'error', ...toastFromError(err) });
    } finally {
      setPwSaving(false);
    }
  }

  return (
    <div className="staff-profile-page">
      <h1>My Profile</h1>
      <div className="sp-crumb">Admin → COG → My Profile &nbsp;·&nbsp; your name, timezone, preferred zone and security</div>

      {meState.status === 'loading' && <div className="sp-note">Loading…</div>}

      {/* Identity */}
      <section className="sp-card" aria-labelledby="sp-identity">
        <h2 id="sp-identity" className="sp-card-title">Identity</h2>
        <div className="sp-row">
          <label className="sp-label" htmlFor="sp-name">Name</label>
          <div className="sp-control">
            <input id="sp-name" className="sp-input" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          </div>
        </div>
        <div className="sp-row">
          <label className="sp-label" htmlFor="sp-email">Email</label>
          <div className="sp-control">
            <input id="sp-email" className="sp-input" type="email" value={me?.email ?? ''} readOnly disabled />
          </div>
        </div>
        <div className="sp-row">
          <span className="sp-label">Role</span>
          <div className="sp-control">
            <input className="sp-input" type="text" value={me ? (ROLE_LABEL[me.role] ?? me.role) : ''} readOnly disabled aria-describedby="sp-role-note" />
            <p id="sp-role-note" className="sp-note-inline">Your role is assigned by an administrator.</p>
          </div>
        </div>
      </section>

      {/* Preferences */}
      <section className="sp-card" aria-labelledby="sp-prefs">
        <h2 id="sp-prefs" className="sp-card-title">Preferences</h2>
        <div className="sp-row">
          <label className="sp-label" htmlFor="sp-tz">Timezone</label>
          <div className="sp-control">
            <select id="sp-tz" className="sp-input sp-select" value={tz} onChange={(e) => setTz(e.target.value)} aria-describedby="sp-tz-note">
              {COMMON_TIME_ZONES.map((z) => (
                <option key={z.value || 'device'} value={z.value}>{z.label}</option>
              ))}
            </select>
            <p id="sp-tz-note" className="sp-note-inline">
              Times across the portal display in this zone. Device timezone is <strong>{deviceTimeZone()}</strong>.
            </p>
          </div>
        </div>
        <div className="sp-row">
          <label className="sp-label" htmlFor="sp-zone">Preferred zone</label>
          <div className="sp-control">
            <select id="sp-zone" className="sp-input sp-select" value={zonePref} onChange={(e) => setZonePref(e.target.value)} aria-describedby="sp-zone-note">
              <option value="">Platform default ({consoleZoneLabel(HOME_ZONE.code)})</option>
              {ZONE_TABLE.map((z) => (
                <option key={z.code} value={z.code}>{consoleZoneLabel(z.code)}</option>
              ))}
            </select>
            <p id="sp-zone-note" className="sp-note-inline">
              Zone switchers and create forms open on this zone. Lists still show all zones until
              you narrow them. A link that names a zone always wins.
            </p>
          </div>
        </div>
        <div className="sp-row">
          <span className="sp-label" />
          <div className="sp-control">
            <button type="button" className="sp-btn" onClick={saveProfile} disabled={!dirty || savingProfile}>
              {savingProfile ? 'Saving…' : 'Save profile'}
            </button>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="sp-card" aria-labelledby="sp-security">
        <h2 id="sp-security" className="sp-card-title">Security</h2>
        <form onSubmit={savePassword}>
          <div className="sp-row">
            <label className="sp-label" htmlFor="sp-curpw">Current password</label>
            <div className="sp-control">
              <input id="sp-curpw" className="sp-input" type={showPw ? 'text' : 'password'} value={curPw} onChange={(e) => setCurPw(e.target.value)} autoComplete="current-password" />
            </div>
          </div>
          <div className="sp-row">
            <label className="sp-label" htmlFor="sp-newpw">New password</label>
            <div className="sp-control">
              <input id="sp-newpw" className="sp-input" type={showPw ? 'text' : 'password'} value={newPw} onChange={(e) => setNewPw(e.target.value)} autoComplete="new-password" />
            </div>
          </div>
          <div className="sp-row">
            <label className="sp-label" htmlFor="sp-confirmpw">Confirm new password</label>
            <div className="sp-control">
              <input id="sp-confirmpw" className="sp-input" type={showPw ? 'text' : 'password'} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} autoComplete="new-password" />
              <label className="sp-showpw"><input type="checkbox" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} /> Show passwords</label>
              <button type="submit" className="sp-btn" disabled={pwSaving}>{pwSaving ? 'Saving…' : 'Change password'}</button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}
